// asthmaFlowLogic.ts
import { MildSectionKey, SeverityLevel } from './utils/asthmaTypes';

interface FlowContext {
  currentSection: MildSectionKey;
  prevSeverity: SeverityLevel | null;
  newSeverity: SeverityLevel | null;
  secondRoundStarted: boolean;
  thirdRoundStarted: boolean;
}

interface FlowResult {
  nextSection: MildSectionKey;
  nextSecondRoundStarted: boolean;
  nextThirdRoundStarted: boolean;
  nextRecheckDone: boolean;
}

/**
 * A pure function that decides what the next step in treatment is
 */
export function getNextStep(ctx: FlowContext): FlowResult {
  const {
    currentSection,
    prevSeverity,
    newSeverity,
    secondRoundStarted,
    thirdRoundStarted
  } = ctx;

  // Defaults: Remain in the same state, indicating that the evaluation has been performed
  const result: FlowResult = {
    nextSection: currentSection,
    nextSecondRoundStarted: secondRoundStarted,
    nextThirdRoundStarted: thirdRoundStarted,
    nextRecheckDone: true, 
  };

  if (!newSeverity) return result;

  // 1. mild -> Moderate (after first assessment)
  if (
    currentSection === 'RECHECK' &&
    prevSeverity === 'MILD' &&
    newSeverity === 'MODERATE'
  ) {
    result.nextSection = 'RECOMMENDATIONS';
    result.nextSecondRoundStarted = false; 
    return result;
  }

  // 2. Medium -> sever (after second round)
  if (
    currentSection === 'RECHECK_2' && 
    prevSeverity === 'MODERATE' &&
    newSeverity === 'SEVERE' &&
    secondRoundStarted
  ) {
    result.nextSection = 'RECOMMENDATIONS_2';
    result.nextThirdRoundStarted = false;
    return result;
  }

  // 3. Started sever -> stayed sever (after first round) -> moved on to treatment 2
  if (
    currentSection === 'RECHECK' &&
    prevSeverity === 'SEVERE' &&
    newSeverity === 'SEVERE' &&
    !secondRoundStarted &&
    !thirdRoundStarted
  ) {
    result.nextSection = 'TREATMENT_2';
    result.nextSecondRoundStarted = true;
    result.nextRecheckDone = false; // A new treatment is started, so "Assessment Completed" is reset.
    return result;
  }

  // 4. Second round sever -> remains sever
  if (
    currentSection === 'RECHECK_2' &&
    prevSeverity === 'SEVERE' &&
    newSeverity === 'SEVERE' &&
    secondRoundStarted &&
    !thirdRoundStarted
  ) {
    result.nextSection = 'RECOMMENDATIONS_2';
    return result;
  }

  // 5. Defaults (Normal flow: Review -> Recommendations)
  if (currentSection === 'RECHECK') {
    result.nextSection = 'RECOMMENDATIONS';
  } else if (currentSection === 'RECHECK_2') {
    result.nextSection = 'RECOMMENDATIONS_2';
  } else if (currentSection === 'RECHECK_3') {
    result.nextSection = 'RECOMMENDATIONS_3';
  }

  return result;
}